import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Wrench, Plus, X, Trash2, Camera, Calendar, User, MapPin, Pencil, AlertTriangle, Printer, Repeat, Tag } from "lucide-react";
import { PeoplePicker } from "@/components/MaintenanceFields";

const today = () => new Date().toISOString().slice(0, 10);
const MANAGE = ["owner", "hr", "manager"];

const STATUS_STYLE = {
  open: "bg-[#FEF3C7] text-[#92400E]",
  in_progress: "bg-[#DBEAFE] text-[#1E40AF]",
  done: "bg-[#D1FAE5] text-[#065F46]",
};
const STATUS_LABEL = { open: "Ανοιχτή", in_progress: "Σε εξέλιξη", done: "Ολοκληρωμένη" };
const TAG_STYLE = {
  sos: "bg-[#FEE2E2] text-[#991B1B]", next_day: "bg-[#FEF3C7] text-[#92400E]", winter: "bg-[#E0EEF6] text-[#1F5673]",
  tactical: "bg-[#EEF1EA] text-[#4F5B45]", recurring: "bg-[#F1EDE6] text-[#45423C]",
};
const UNIT_SHORT = { days: "ημέρες", weeks: "εβδομάδες", months: "μήνες", years: "έτη" };

const TagChips = ({ t, tagName }) => (
  <div className="flex flex-wrap gap-1 mt-1.5">
    {(t.tags || []).map((g) => (
      <span key={g} data-testid={`task-tag-${t.id}-${g}`} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${TAG_STYLE[g] || "bg-[#F1EDE6]"}`}>
        {g === "recurring" ? `↻ κάθε ${t.repeat_every} ${UNIT_SHORT[t.repeat_unit] || ""}` : tagName[g] || g}
      </span>
    ))}
  </div>
);

const blankForm = () => ({
  villa: "", location: "", title: "", description: "", trade: "general",
  assignee: "", task_date: today(), due_date: "", status: "open", photo_file_ids: [], audio_file_ids: [], notes: "",
  tags: [], repeat_every: 1, repeat_unit: "months", assignee_ids: [],
});

export default function MaintenanceTasks() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = MANAGE.includes(user?.role);
  const [params] = useSearchParams();
  const [filters, setFilters] = useState({ status: "", villa: "", trade: "", tag: params.get("tag") || "" });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [view, setView] = useState("tag");

  const { data: meta } = useQuery({ queryKey: ["maint-meta"], queryFn: async () => (await api.get("/maintenance/meta")).data });
  const { data: people = [] } = useQuery({ queryKey: ["maint-people"], queryFn: async () => (await api.get("/maintenance/people")).data });
  const { data: tasks = [] } = useQuery({
    queryKey: ["maint-tasks", filters],
    queryFn: async () => {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && p.append(k, v));
      return (await api.get(`/maintenance/tasks?${p.toString()}`)).data;
    },
  });

  const villas = meta?.villas || [];
  const trades = meta?.trades || [];
  const statuses = meta?.statuses || [];
  const tagList = meta?.tags || [];
  const units = meta?.repeat_units || [];
  const tagName = Object.fromEntries(tagList.map((g) => [g.id, g.name]));
  const toggleTag = (g) => setForm((p) => ({ ...p, tags: p.tags.includes(g) ? p.tags.filter((x) => x !== g) : [...p.tags, g] }));

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form };
      if (editingId) return (await api.put(`/maintenance/tasks/${editingId}`, body)).data;
      return (await api.post("/maintenance/tasks", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maint-tasks"] });
      toast.success(editingId ? "Η εργασία ενημερώθηκε" : "Η εργασία καταχωρήθηκε");
      setOpen(false); setEditingId(null); setForm(blankForm());
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const quickStatus = useMutation({
    mutationFn: async ({ task, status }) => (await api.put(`/maintenance/tasks/${task.id}`, { ...task, status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["maint-tasks"] }); toast.success("Η κατάσταση ενημερώθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/maintenance/tasks/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["maint-tasks"] }); toast.success("Διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(data.file_id);
      }
      setForm((p) => ({ ...p, photo_file_ids: [...p.photo_file_ids, ...ids] }));
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openNew = () => { setForm(blankForm()); setEditingId(null); setOpen(true); };
  const openEdit = (t) => {
    setForm({
      villa: t.villa || "", location: t.location || "", title: t.title || "", description: t.description || "",
      trade: t.trade || "general", assignee: t.assignee || "", task_date: t.task_date || today(),
      due_date: t.due_date || "", status: t.status || "open", photo_file_ids: t.photo_file_ids || [], notes: t.notes || "",
      audio_file_ids: t.audio_file_ids || [], tags: t.tags || [], repeat_every: t.repeat_every || 1, repeat_unit: t.repeat_unit || "months",
      assignee_ids: t.assignee_ids || [],
    });
    setEditingId(t.id); setOpen(true);
  };

  const canSave = form.villa && form.title && form.task_date && !save.isPending;

  const counts = useMemo(() => {
    const c = { open: 0, in_progress: 0, done: 0, overdue: 0 };
    tasks.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; if (t.overdue) c.overdue += 1; });
    return c;
  }, [tasks]);

  const inp = "w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";
  const lbl = "text-xs uppercase tracking-wider text-[#6F6A60] font-semibold block mb-1";

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Facilities</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><Wrench className="h-7 w-7" /> Εργασίες Συντήρησης</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Καταγράψτε εργασίες ανά βίλα & χώρο, με φωτογραφίες, υπεύθυνο ειδικότητας, ημερομηνία & προθεσμία.</p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button data-testid="print-report-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm font-semibold text-[#45423C]">
            <Printer className="h-4 w-4" /> Εκτύπωση αναφοράς
          </button>
          <button data-testid="new-task-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#56524A]">
            <Plus className="h-4 w-4" /> Νέα εργασία
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold">Ανοιχτές: {counts.open}</span>
        <span className="px-3 py-1.5 rounded-full bg-[#DBEAFE] text-[#1E40AF] font-semibold">Σε εξέλιξη: {counts.in_progress}</span>
        <span className="px-3 py-1.5 rounded-full bg-[#D1FAE5] text-[#065F46] font-semibold">Ολοκληρωμένες: {counts.done}</span>
        {counts.overdue > 0 && <span className="px-3 py-1.5 rounded-full bg-[#FEE2E2] text-[#991B1B] font-semibold inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Εκπρόθεσμες: {counts.overdue}</span>}
      </div>

      <div className="flex flex-wrap gap-3">
        <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={`${inp} sm:w-44`}>
          <option value="">Όλες οι καταστάσεις</option>
          {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select data-testid="filter-villa" value={filters.villa} onChange={(e) => setFilters({ ...filters, villa: e.target.value })} className={`${inp} sm:w-44`}>
          <option value="">Όλες οι βίλες</option>
          {villas.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select data-testid="filter-trade" value={filters.trade} onChange={(e) => setFilters({ ...filters, trade: e.target.value })} className={`${inp} sm:w-48`}>
          <option value="">Όλες οι ειδικότητες</option>
          {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select data-testid="filter-tag" value={filters.tag} onChange={(e) => setFilters({ ...filters, tag: e.target.value })} className={`${inp} sm:w-44`}>
          <option value="">Όλα τα tags</option>
          {tagList.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          <option value="none">Χωρίς tag</option>
        </select>
        <div className="ml-auto inline-flex rounded-xl border border-[#E3DED4] overflow-hidden self-center">
          <button data-testid="view-tag" onClick={() => setView("tag")} className={`px-4 py-2 text-sm font-medium ${view === "tag" ? "bg-[#45423C] text-white" : "bg-white text-[#45423C]"}`}>Ανά Tag</button>
          <button data-testid="view-list" onClick={() => setView("list")} className={`px-4 py-2 text-sm font-medium ${view === "list" ? "bg-[#45423C] text-white" : "bg-white text-[#45423C]"}`}>Λίστα</button>
          <button data-testid="view-villa" onClick={() => setView("villa")} className={`px-4 py-2 text-sm font-medium ${view === "villa" ? "bg-[#45423C] text-white" : "bg-white text-[#45423C]"}`}>Ανά Βίλα</button>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E3DED4] p-12 text-center text-[#6F6A60]" data-testid="no-tasks">
          <Wrench className="h-8 w-8 mx-auto mb-3 opacity-40" />
          Δεν υπάρχουν εργασίες. Πατήστε «Νέα εργασία» για να καταγράψετε την πρώτη.
        </div>
      ) : view === "tag" ? (
        <div className="space-y-4" data-testid="tag-view">
          {[...tagList, { id: "none", name: "Χωρίς tag" }].map((g) => ({ ...g, items: tasks.filter((t) => (g.id === "none" ? !(t.tags || []).length : (t.tags || []).includes(g.id))) }))
            .filter((g) => g.items.length).map((g) => (
              <div key={g.id} className="rounded-2xl bg-white border border-[#E3DED4] p-5 break-inside-avoid" data-testid={`tag-group-${g.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-xl text-[#45423C] inline-flex items-center gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${TAG_STYLE[g.id] || "bg-[#F1EDE6] text-[#6F6A60]"}`}>{g.name}</span>
                  </h3>
                  <span className="text-xs text-[#6F6A60]">{g.items.length} εργασίες · {g.items.filter((t) => t.status !== "done").length} ανοιχτές</span>
                </div>
                <div className="mt-3 divide-y divide-[#F1EDE6]">
                  {g.items.map((t) => (
                    <button key={t.id} onClick={() => openEdit(t)} data-testid={`tag-task-${g.id}-${t.id}`} className="w-full text-left py-2 px-1 flex items-center justify-between gap-2 hover:bg-[#F7F5F1] rounded">
                      <div className="min-w-0">
                        <div className="text-sm text-[#45423C] truncate">{t.title} <span className="text-[#8A8577]">· {t.villa_name}{t.location ? ` · ${t.location}` : ""}</span></div>
                        <div className="text-xs text-[#6F6A60] truncate">{t.trade_name}{t.due_date ? ` · να γίνει έως ${t.due_date}` : ` · ${t.task_date}`}{t.next_date ? ` · επόμενη ${t.next_date}` : ""}</div>
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_STYLE[t.status]} ${t.overdue ? "ring-1 ring-[#FCA5A5]" : ""}`}>{STATUS_LABEL[t.status]}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
        </div>
      ) : view === "villa" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="villa-view">
          {villas.map((v) => ({ ...v, items: tasks.filter((t) => t.villa === v.id) })).filter((v) => v.items.length).map((v) => {
            const c = { open: 0, in_progress: 0, done: 0, overdue: 0 };
            v.items.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; if (t.overdue) c.overdue += 1; });
            return (
              <div key={v.id} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`villa-card-${v.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-xl text-[#45423C] inline-flex items-center gap-2"><MapPin className="h-5 w-5 text-[#8A8577]" /> {v.name}</h3>
                  <span className="text-xs text-[#6F6A60]">{v.items.length} εργασίες</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2 text-[10px] font-semibold">
                  {c.open > 0 && <span className="px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">Ανοιχτές {c.open}</span>}
                  {c.in_progress > 0 && <span className="px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E40AF]">Σε εξέλιξη {c.in_progress}</span>}
                  {c.done > 0 && <span className="px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#065F46]">Ολοκληρ. {c.done}</span>}
                  {c.overdue > 0 && <span className="px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B]">Εκπρόθεσμες {c.overdue}</span>}
                </div>
                <div className="mt-3 divide-y divide-[#F1EDE6]">
                  {v.items.map((t) => (
                    <button key={t.id} onClick={() => openEdit(t)} data-testid={`villa-task-${t.id}`} className="w-full text-left py-2 px-1 flex items-center justify-between gap-2 hover:bg-[#F7F5F1] rounded">
                      <div className="min-w-0">
                        <div className="text-sm text-[#45423C] truncate">{t.title}</div>
                        <div className="text-xs text-[#6F6A60] truncate">{t.location ? `${t.location} · ` : ""}{t.trade_name}{t.due_date ? ` · προθεσμία ${t.due_date}` : ""}</div>
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_STYLE[t.status]} ${t.overdue ? "ring-1 ring-[#FCA5A5]" : ""}`}>{STATUS_LABEL[t.status]}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {tasks.map((t) => (
            <div key={t.id} className={`rounded-2xl bg-white border p-4 ${t.overdue ? "border-[#FCA5A5] ring-1 ring-[#FCA5A5]" : "border-[#E3DED4]"}`} data-testid={`task-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#8A8577] font-semibold inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.villa_name}{t.location ? ` · ${t.location}` : ""}</div>
                  <h3 className="font-medium text-[#45423C] mt-0.5">{t.title}</h3>
                  <TagChips t={t} tagName={tagName} />
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider shrink-0 ${STATUS_STYLE[t.status]}`}>{STATUS_LABEL[t.status]}</span>
              </div>

              {t.description && <p className="text-sm text-[#6F6A60] mt-2 line-clamp-3">{t.description}</p>}

              {t.photo_file_ids?.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {t.photo_file_ids.map((fid) => (
                    <img key={fid} src={fileUrl(fid)} alt="" onClick={() => setPreview(fid)}
                      className="h-16 w-16 rounded-lg object-cover border border-[#E3DED4] cursor-pointer hover:opacity-80" data-testid={`task-photo-${t.id}`} />
                  ))}
                </div>
              )}

              {t.audio_file_ids?.map((fid) => <audio key={fid} controls src={fileUrl(fid)} className="h-8 w-full mt-2" />)}

              <div className="mt-3 space-y-1 text-xs text-[#6F6A60]">
                <div className="inline-flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> {t.trade_name}{t.assignee ? ` — ${t.assignee}` : ""}</div>
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {t.task_date}</span>
                  {t.due_date && <span className={`inline-flex items-center gap-1.5 ${t.overdue ? "text-[#991B1B] font-semibold" : ""}`}>Προθεσμία: {t.due_date}</span>}
                </div>
                {t.next_date && <div className="inline-flex items-center gap-1.5" data-testid={`task-next-${t.id}`}><Repeat className="h-3.5 w-3.5" /> Επόμενη: {t.next_date}</div>}
                <div className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {t.created_by_name}</div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F1EDE6]">
                <select data-testid={`status-${t.id}`} value={t.status} onChange={(e) => quickStatus.mutate({ task: t, status: e.target.value })}
                  className="rounded-lg border border-[#E3DED4] px-2 py-1 text-xs outline-none focus:border-[#45423C]">
                  {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <div className="flex items-center gap-1">
                  <button data-testid={`edit-${t.id}`} onClick={() => openEdit(t)} className="p-1.5 rounded-lg bg-[#F1EDE6] text-[#6F6A60] hover:text-[#45423C]" title="Επεξεργασία"><Pencil className="h-4 w-4" /></button>
                  {canDelete && <button data-testid={`delete-${t.id}`} onClick={() => { if (window.confirm("Διαγραφή εργασίας;")) del.mutate(t.id); }} className="p-1.5 rounded-lg hover:bg-[#FEF2F2] text-[#991B1B]/70" title="Διαγραφή"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)} data-testid="task-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl text-[#45423C]">{editingId ? "Επεξεργασία εργασίας" : "Νέα εργασία συντήρησης"}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6]" data-testid="close-modal"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Βίλα *</label>
                <select data-testid="form-villa" value={form.villa} onChange={(e) => setForm({ ...form, villa: e.target.value })} className={inp}>
                  <option value="">— Επιλέξτε —</option>
                  {villas.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Χώρος / Δωμάτιο</label>
                <input data-testid="form-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="π.χ. Master μπάνιο, Κουζίνα" className={inp} />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Τύπος / Τίτλος εργασίας *</label>
                <input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="π.χ. Διαρροή στο νιπτήρα" className={inp} />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Περιγραφή</label>
                <textarea data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={inp} />
              </div>
              <div>
                <label className={lbl}>Ειδικότητα υπευθύνου</label>
                <select data-testid="form-trade" value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} className={inp}>
                  {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Όνομα υπευθύνου (προαιρετικό)</label>
                <input data-testid="form-assignee" value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="π.χ. Γιώργος (υδραυλικός)" className={inp} />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}><Tag className="inline h-3 w-3" /> Tags</label>
                <div className="flex flex-wrap gap-2">
                  {tagList.map((g) => (
                    <button type="button" key={g.id} data-testid={`form-tag-${g.id}`} onClick={() => toggleTag(g.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.tags.includes(g.id) ? `${TAG_STYLE[g.id]} border-transparent ring-2 ring-[#45423C]/30` : "bg-white border-[#E3DED4] text-[#6F6A60]"}`}>
                      {g.name}
                    </button>
                  ))}
                </div>
                {form.tags.includes("recurring") && (
                  <div className="flex items-center gap-2 mt-3 text-sm text-[#45423C]" data-testid="form-repeat">
                    <Repeat className="h-4 w-4" /> Κάθε
                    <input data-testid="form-repeat-every" type="number" min="1" max="365" value={form.repeat_every}
                      onChange={(e) => setForm({ ...form, repeat_every: Number(e.target.value) || 1 })} className={`${inp} !w-20`} />
                    <select data-testid="form-repeat-unit" value={form.repeat_unit} onChange={(e) => setForm({ ...form, repeat_unit: e.target.value })} className={`${inp} !w-36`}>
                      {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Ανάθεση σε (εμφανίζεται στα «Οι Εργασίες μου»)</label>
                <PeoplePicker testid="form-assignees" people={people} value={form.assignee_ids} onChange={(v) => setForm({ ...form, assignee_ids: v })} />
              </div>
              <div>
                <label className={lbl}>Αναφορά στις *</label>
                <input data-testid="form-date" type="date" value={form.task_date} onChange={(e) => setForm({ ...form, task_date: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>Να γίνει έως</label>
                <input data-testid="form-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>Κατάσταση</label>
                <select data-testid="form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inp}>
                  {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Φωτογραφίες</label>
                <label className="inline-flex items-center gap-2 rounded-lg border border-[#E3DED4] px-4 py-2 text-sm cursor-pointer hover:border-[#B9AE9A] w-fit">
                  <Camera className="h-4 w-4" /> {uploading ? "Ανέβασμα…" : "Προσθήκη φωτογραφιών"}
                  <input data-testid="form-photos" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={uploading} />
                </label>
                {form.photo_file_ids.length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {form.photo_file_ids.map((fid) => (
                      <div key={fid} className="relative">
                        <img src={fileUrl(fid)} alt="" className="h-20 w-20 rounded-lg object-cover border border-[#E3DED4]" />
                        <button onClick={() => setForm((p) => ({ ...p, photo_file_ids: p.photo_file_ids.filter((x) => x !== fid) }))}
                          className="absolute -top-2 -right-2 bg-[#991B1B] text-white rounded-full p-0.5" data-testid={`remove-photo-${fid}`}><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setOpen(false)} className="rounded-xl border border-[#E3DED4] px-5 py-2.5 text-sm font-medium hover:border-[#B9AE9A]">Άκυρο</button>
              <button data-testid="save-task-btn" onClick={() => save.mutate()} disabled={!canSave} className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
                {save.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="photo-lightbox">
          <img src={fileUrl(preview)} alt="" className="max-h-[90vh] max-w-[90vw] rounded-lg" />
        </div>
      )}
    </div>
  );
}
